import React, { useRef, useState } from "react";
import { Plus, Trash2, DollarSign, Info, RotateCcw, Lightbulb, Check, X, ChevronDown, ChevronRight, Download, FileSpreadsheet, Link2 } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import AIObservationInlineCard from "@ui/AIObservationInlineCard";
import ObservationFlagIcon from "@ui/ObservationFlagIcon";
import { useShoppingList } from "@data/hooks/useShoppingList";
import Attachments from "../components/Attachments";
import PlanLineItems from "../components/PlanLineItems";
import PlanLinks from "../components/PlanLinks";
import MoneyInput from "@ui/MoneyInput";
import { formatMoney } from "@shared/formatters/money";
import { invoiceTotalsByCategory } from "@domain/invoices/payments";
import { useLatestAIReview } from "@features/chat/hooks/useLatestAIReview";
import { defaultBudgetCategories } from "@domain/project/projectData";
import { defaultRecommendations } from "@domain/budget/costSavings";
import { buildPlanB, planBSqFt } from "@domain/budget/planB";
import { exportBudgetCsv, exportBudgetXlsx } from "../utils/exportBudget";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import useKeepInPlace from "@shared/hooks/useKeepInPlace";
import useInvoices from "@features/invoices/hooks/useInvoices";

// Build a lookup of recommendations by budget id
const recsMap = {};
defaultRecommendations.forEach((r) => {
  if (!recsMap[r.budgetId]) recsMap[r.budgetId] = [];
  recsMap[r.budgetId].push(r);
});

const riskColors = {
  low: "text-emerald-400 bg-emerald-500/15",
  medium: "text-amber-400 bg-amber-500/15",
  high: "text-red-400 bg-red-500/15",
};

const effortColors = {
  low: "text-cyan-400 bg-cyan-500/15",
  medium: "text-blue-400 bg-blue-500/15",
  high: "text-purple-400 bg-purple-500/15",
};

// Plan B defaults: build from Plan A defaults
const defaultPlanBBudget = buildPlanB(defaultBudgetCategories);

// Backward-compat: items from Firestore before the two-field schema have
// `estimated` + optional `costType`. Normalise on load; first edit writes
// the new schema back so migration is lazy and invisible to the user.
const MAT_IDS_LEGACY = new Set([12, 16, 17, 13, 14, 23, 24]);
function normalizeCat(c) {
  if (c.estimatedMat !== undefined || c.estimatedLabor !== undefined) return c;
  const isMat = c.costType === "mat" || (!c.costType && MAT_IDS_LEGACY.has(c.id));
  return {
    ...c,
    estimatedMat:   isMat ? (c.estimated || 0) : 0,
    estimatedLabor: isMat ? 0 : (c.estimated || 0),
  };
}
const catTotal = (c) => (c.estimatedMat || 0) + (c.estimatedLabor || 0);

function BudgetTable({ budget, setBudget, sqFt, dismissed, setDismissed, actualFromInvoices = {}, invoiceTotals = {}, invoicesByCategory = {}, shopping }) {
  const [hoveredNote, setHoveredNote] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  // Opening a row closes the one that was open (possibly above it); keep
  // the tapped row under the pointer instead of letting it jump up.
  const keepInPlace = useKeepInPlace();
  const toggleRow = (e, id) => {
    keepInPlace(e.currentTarget);
    setExpandedRow((cur) => (cur === id ? null : id));
  };
  const [quickName, setQuickName] = useState("");
  const quickRef = useRef(null);
  const { observationsFor } = useLatestAIReview();

  // Normalise once. `base` is what gets written back; `norm` is for display
  // and totals — lines with priced line items take their Materials from
  // those items (incl. tax + shipping), while the typed estimate stays
  // untouched in `base` as a fallback and for comparison.
  const { items: lineItems, setItems: setLineItems, taxRate, byCategory } = shopping;
  const base = budget.map(normalizeCat);
  const norm = base.map((c) =>
    byCategory[c.id] ? { ...c, estimatedMat: byCategory[c.id].total } : c
  );
  const manualMat = (id) => base.find((c) => c.id === id)?.estimatedMat || 0;
  const itemizedBadge = (c) => (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-semibold"
      title={`Sum of ${byCategory[c.id].count} line item${byCategory[c.id].count === 1 ? "" : "s"} incl. tax + shipping`}
    >
      ✦{byCategory[c.id].count}
    </span>
  );

  // "Actual" is money actually paid. Once a row has invoices they take over
  // (the paper trail is the source of truth) and Actual is what's been paid
  // on them, partial payments included; otherwise it's the typed-in actual.
  // Unpaid invoice balances show separately as "due".
  const isInvoiceDerived = (c) => (actualFromInvoices[c.id] || 0) > 0;
  const effectiveActual = (c) =>
    isInvoiceDerived(c) ? invoiceTotals[c.id]?.paid || 0 : c.actual || 0;
  const invoicedFor = (c) => actualFromInvoices[c.id] || 0;
  const dueFor = (c) =>
    isInvoiceDerived(c) ? Math.round((invoicedFor(c) - effectiveActual(c)) * 100) / 100 : 0;

  const totalMat    = norm.reduce((s, c) => s + (c.estimatedMat || 0), 0);
  const totalLabor  = norm.reduce((s, c) => s + (c.estimatedLabor || 0), 0);
  const totalPlanned = totalMat + totalLabor;
  const totalActual = norm.reduce((s, c) => s + effectiveActual(c), 0);
  const totalDue    = norm.reduce((s, c) => s + dueFor(c), 0);
  const variance    = Math.round((totalPlanned - totalActual) * 100) / 100;
  const estPerSqFt  = sqFt > 0 ? Math.round(totalPlanned / sqFt) : 0;
  const actualPerSqFt = sqFt > 0 ? Math.round(totalActual / sqFt) : 0;

  const totalPotentialSavings = defaultRecommendations
    .filter((r) => {
      if (dismissed[r.id]) return false;
      const cat = norm.find((c) => c.id === r.budgetId);
      return cat && catTotal(cat) > r.proposedCost;
    })
    .reduce((sum, r) => sum + r.savings, 0);

  const updateField = (id, field, raw) => {
    const val = parseFloat(raw) || 0;
    setBudget(base.map((c) => c.id === id ? { ...c, [field]: val } : c));
  };
  const updateActual = (id, raw) => updateField(id, "actual", raw);
  const updateMat    = (id, raw) => updateField(id, "estimatedMat", raw);
  const updateLabor  = (id, raw) => updateField(id, "estimatedLabor", raw);
  const updateNotes  = (id, notes) => setBudget(base.map((c) => c.id === id ? { ...c, notes } : c));
  const updateLinks  = (id, links) => setBudget(base.map((c) => c.id === id ? { ...c, links } : c));
  const linksField = (cat) => <PlanLinks links={cat.links || []} onChange={(links) => updateLinks(cat.id, links)} />;
  // Collapsed-row hint that a line has links; clicking opens the row.
  const linksBadge = (cat) =>
    (cat.links?.length ?? 0) > 0 && (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-400" title={`${cat.links.length} link${cat.links.length === 1 ? "" : "s"}`}>
        <Link2 className="w-3 h-3" />{cat.links.length}
      </span>
    );

  // Editable in place; grows with its content (field-sizing), so a long
  // note reads like the old static text until you click into it. Colors use
  // theme vars directly — focus:text-gray-* has no light-theme override.
  const notesField = (cat, textSize) => (
    <textarea
      value={cat.notes || ""}
      onChange={(e) => updateNotes(cat.id, e.target.value)}
      placeholder="Add a note…"
      rows={1}
      className={`w-full ${textSize} text-[color:var(--text-muted)] focus:text-[color:var(--text-heading)] leading-relaxed bg-transparent rounded px-1.5 py-0.5 -mx-1.5 resize-none outline-none border border-transparent hover:border-gray-600 focus:ring-1 focus:ring-indigo-500 placeholder-gray-600`}
      style={{ fieldSizing: "content" }}
    />
  );

  const quickAdd = (e) => {
    const name = quickName.trim();
    if (!name) return;
    setBudget([...base, { id: Date.now(), name, estimatedMat: 0, estimatedLabor: 0, actual: 0 }]);
    setQuickName("");
    quickRef.current?.focus();
  };

  const deleteCategory = (id) => setBudget(base.filter((c) => c.id !== id));

  // Approve: reduce the larger of the two planned fields to proposedCost.
  const approveSaving = (rec) => {
    setBudget(base.map((c) => {
      if (c.id !== rec.budgetId) return c;
      const proposed = rec.proposedCost;
      if ((c.estimatedMat || 0) >= (c.estimatedLabor || 0)) {
        return { ...c, estimatedMat: proposed };
      }
      return { ...c, estimatedLabor: proposed };
    }));
    setDismissed((prev) => ({ ...prev, [rec.id]: true }));
  };
  const dismissSaving = (recId) => setDismissed((prev) => ({ ...prev, [recId]: true }));

  const inputStyle = { background: 'var(--bg-input)', border: '1px solid var(--border-input)' };
  const accentColor = "cyan";

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
        <InfoBentoCard cornerGlow frostedGradient accent={accentColor} className="h-full">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Planned</p>
          <p className={`text-base md:text-lg font-bold mt-1 text-${accentColor}-400`}>{formatMoney(totalPlanned)}</p>
          <p className="text-[10px] text-gray-500 mb-2"><span className="font-mono">${estPerSqFt}/ft²</span></p>
          <div className="hidden md:flex gap-3">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-gray-600 font-semibold">Materials</p>
              <p className="text-xs font-mono font-semibold text-blue-400">{formatMoney(totalMat)}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-gray-600 font-semibold">Labor/Svcs</p>
              <p className="text-xs font-mono font-semibold text-purple-400">{formatMoney(totalLabor)}</p>
            </div>
          </div>
        </InfoBentoCard>
        <InfoBentoCard cornerGlow frostedGradient accent="amber" className="h-full">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Actuals</p>
          <p className="text-base md:text-lg font-bold text-amber-400 mt-1">{formatMoney(totalActual)}</p>
          <p className="text-[10px] text-gray-500">
            <span className="font-mono">${actualPerSqFt}/ft²</span>
            <span className="hidden md:inline"> &middot; {totalPlanned > 0 ? `${Math.round((totalActual / totalPlanned) * 100)}% of planned` : ""}</span>
          </p>
          {totalDue > 0 && (
            <p className="text-[10px] font-mono mt-0.5 text-amber-400 font-semibold" title="Invoiced but not yet paid">
              + {formatMoney(totalDue)} due on invoices
            </p>
          )}
        </InfoBentoCard>
        <InfoBentoCard cornerGlow frostedGradient accent={variance >= 0 ? "green" : "rose"} className="h-full">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Remaining</p>
          <p className={`text-base md:text-lg font-bold mt-1 ${variance >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {formatMoney(variance)}
          </p>
        </InfoBentoCard>
      </div>

      {/* Savings Banner */}
      {totalPotentialSavings > 0 && (
        <InfoBentoCard cornerGlow frostedGradient accent="green" className="mb-6">
          <div className="flex items-center gap-3">
            <Lightbulb className="w-4 h-4 text-emerald-400" />
            <div>
              <p className="text-xs font-semibold text-gray-200">
                Up to <span className="text-emerald-400 font-mono">{formatMoney(totalPotentialSavings)}</span> in potential savings identified
              </p>
              <p className="text-[11px] text-gray-500">Expand any budget row to see recommendations.</p>
            </div>
          </div>
        </InfoBentoCard>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
        <button onClick={() => exportBudgetCsv(norm)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-gray-200 hover:bg-white/[0.05]" style={inputStyle} title="Export as CSV">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
        <button onClick={() => exportBudgetXlsx(norm)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-gray-200 hover:bg-white/[0.05]" style={inputStyle} title="Export as Excel">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
        </button>
        <button onClick={() => { if (window.confirm("Reset budget to defaults?")) setBudget(defaultPlanBBudget); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-gray-400 hover:text-gray-200" style={inputStyle} title="Reset to defaults">
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Budget cards (mobile) */}
      {/* Mobile — Jira-style slim rows inside one container */}
      <div className="md:hidden overflow-hidden rounded-lg" style={{ border: '1px solid var(--border-card-frosted)', background: 'var(--bg-card-frosted)' }}>
        {/* Column headers */}
        <div className="flex items-center px-3 py-1.5" style={{ borderBottom: '2px solid var(--border-subtle)' }}>
          <span className="flex-1 text-[9px] uppercase tracking-widest text-gray-500 font-semibold">Item</span>
          <span className="w-[66px] text-right text-[9px] uppercase tracking-widest text-blue-500/70 font-semibold">Mat</span>
          <span className="w-[66px] text-right text-[9px] uppercase tracking-widest text-purple-500/70 font-semibold">L+S</span>
          <span className="w-5" />
        </div>

        {norm.map((cat) => {
          const planned = catTotal(cat);
          const effActual = effectiveActual(cat);
          const fromInvoices = isInvoiceDerived(cat);
          const catInvoices = invoicesByCategory[cat.id] || [];
          const catVariance = Math.round((planned - effActual) * 100) / 100;
          const recs = (recsMap[cat.id] || []).filter(r => !dismissed[r.id] && planned > r.proposedCost);
          const isExpanded = expandedRow === cat.id;
          const catObservations = observationsFor("budget", cat.id);
          const hasDetail = recs.length > 0 || catObservations.length > 0 || cat.notes;
          const isOver = effActual > planned && planned > 0;
          const hasTip = recs.length > 0;

          return (
            <React.Fragment key={cat.id}>
              <div
                className="flex items-center px-0 group"
                style={{ borderBottom: '1px solid var(--border-row)', borderLeft: `3px solid ${isOver ? '#ef4444' : hasTip ? '#10b981' : 'var(--border-row)'}` }}
              >
                {/* Name cell — tap to toggle detail */}
                <button
                  className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-2.5 text-left"
                  onClick={(e) => toggleRow(e, cat.id)}
                >
                  <span className="text-xs text-gray-200 truncate leading-none">{cat.name}</span>
                  {linksBadge(cat)}
                  {catObservations.length > 0 && !isExpanded && <ObservationFlagIcon observations={catObservations} size="sm" />}
                  {hasTip && !isExpanded && <span className="text-[8px] px-1 rounded bg-emerald-500/20 text-emerald-400 flex-shrink-0">{recs.length}✦</span>}
                </button>
                {/* Mat */}
                {byCategory[cat.id] ? (
                  <span className="w-[66px] text-right text-[11px] font-mono text-blue-300 py-2.5" title="From line items">
                    ✦{formatMoney((cat.estimatedMat || 0))}
                  </span>
                ) : (
                  <MoneyInput value={(cat.estimatedMat || 0)} onChange={(v) => updateMat(cat.id, v)}
                    className="w-[66px] text-right text-[11px] font-mono text-blue-300 bg-transparent outline-none focus:bg-blue-500/10 rounded py-2.5"
                  />
                )}
                {/* L+S */}
                <MoneyInput value={(cat.estimatedLabor || 0)} onChange={(v) => updateLabor(cat.id, v)}
                  className="w-[66px] text-right text-[11px] font-mono text-purple-300 bg-transparent outline-none focus:bg-purple-500/10 rounded py-2.5 pr-1"
                />
                <button onClick={() => deleteCategory(cat.id)} className="w-5 flex-shrink-0 flex items-center justify-center text-gray-800 hover:text-red-400 pr-1" aria-label="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>

              {/* Expanded detail panel */}
              {isExpanded && (
                <div className="px-3 py-2 space-y-1.5" style={{ background: 'var(--bg-row-hover)', borderBottom: '1px solid var(--border-subtle)', borderLeft: '3px solid var(--border-subtle)' }}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Actual</span>
                      <MoneyInput value={cat.actual} onChange={(v) => updateActual(cat.id, v)}
                        className="w-20 text-right text-[11px] font-mono text-gray-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                        style={inputStyle} />
                      {fromInvoices && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-semibold">
                          ✦ {formatMoney(effActual)} paid on {catInvoices.length} invoice{catInvoices.length === 1 ? "" : "s"}{dueFor(cat) > 0 ? ` · ${formatMoney(dueFor(cat))} due` : ""}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Var</span>
                      <span className={`text-[11px] font-mono font-semibold ${catVariance >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatMoney(catVariance)}</span>
                    </div>
                    <div className="ml-auto"><Attachments budgetId={cat.id} /></div>
                  </div>
                  {catInvoices.length > 0 && (
                    <div className="space-y-0.5">
                      {catInvoices.map((iv) => (
                        <a
                          key={iv.id + iv.amount}
                          href={iv.pdfDownloadURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between text-[10px] px-2 py-1 rounded hover:bg-white/[0.04]"
                        >
                          <span className="truncate text-gray-300">{iv.vendor || "—"} · {iv.status}</span>
                          <span className="font-mono text-cyan-400">{formatMoney(iv.amount)}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {notesField(cat, "text-xs")}
                  {linksField(cat)}
                  {catObservations.map((obs, i) => (
                    <AIObservationInlineCard key={`obs-${i}`} observation={obs} entityLabel={`the ${cat.name} budget line`} />
                  ))}
                  {recs.map((rec) => (
                    <div key={rec.id} className="rounded p-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-row)' }}>
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <Lightbulb className="w-3 h-3 text-emerald-400" />
                        <span className="text-[11px] font-semibold text-gray-200">{rec.title}</span>
                        <span className={`text-[9px] px-1 rounded ${riskColors[rec.risk]}`}>{rec.risk}</span>
                      </div>
                      <p className="text-[10px] text-gray-500 mb-1.5">
                        {formatMoney(rec.currentCost)} → <span className="text-emerald-400 font-semibold">{formatMoney(rec.proposedCost)}</span>
                        <span className="ml-1 text-emerald-400">save {formatMoney(rec.savings)}</span>
                      </p>
                      <div className="flex gap-2">
                        <button onClick={() => approveSaving(rec)} className="flex-1 text-[10px] font-medium py-1 rounded bg-emerald-600 text-white">Approve</button>
                        <button onClick={() => dismissSaving(rec.id)} className="px-3 text-[10px] rounded text-gray-400" style={inputStyle}>✕</button>
                      </div>
                    </div>
                  ))}
                  <PlanLineItems category={cat} items={lineItems} taxRate={taxRate} setItems={setLineItems} manualEstimate={manualMat(cat.id)} />
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Totals */}
        <div className="flex items-center px-3 py-2" style={{ borderTop: '2px solid var(--border-subtle)', background: 'var(--bg-row-hover)' }}>
          <span className="flex-1 text-[11px] font-semibold text-gray-300">Total</span>
          <span className="w-[66px] text-right text-[11px] font-mono font-semibold text-blue-300">{formatMoney(totalMat)}</span>
          <span className="w-[66px] text-right text-[11px] font-mono font-semibold text-purple-300 pr-1">{formatMoney(totalLabor)}</span>
          <span className="w-5" />
        </div>
        <div className="flex items-center gap-3 px-3 pb-2 text-[10px] font-mono">
          <span className="text-gray-500">Actual {formatMoney(totalActual)}</span>
          <span className="text-gray-600">·</span>
          <span className="text-gray-500">Left <span className={`font-semibold ${variance >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatMoney(variance)}</span></span>
        </div>

        {/* + Create (Jira-style) */}
        <div className="flex items-center gap-2 px-3 py-2" style={{ borderTop: '1px solid var(--border-row)' }}>
          <Plus className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
          <input ref={quickRef} type="text" value={quickName}
            onChange={(e) => setQuickName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") quickAdd(); }}
            placeholder="Create"
            className="flex-1 text-xs bg-transparent focus:outline-none text-gray-300 placeholder-gray-500" />
        </div>
      </div>

      {/* Budget Table (desktop) */}
      <InfoBentoCard cornerGlow frostedGradient accent={accentColor} className="hidden md:block">
        {/* overflow-x-clip rather than auto so the sticky header below works */}
        <div className="overflow-x-clip -mx-4 -mb-4">
          <table className="w-full">
            {/* Sticky under the app TopBar; box-shadow instead of a border
                because collapsed table borders don't travel with sticky rows. */}
            <thead className="sticky z-20" style={{ top: 'var(--topbar-h)', background: 'var(--bg-root)', boxShadow: '0 1px 0 var(--border-row)' }}>
              <tr>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Category</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-blue-500/70 font-semibold">Materials</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-purple-500/70 font-semibold">Labor/Svcs</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Actual</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Variance</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {norm.map((cat) => {
                const planned = catTotal(cat);
                const effActual = effectiveActual(cat);
                const fromInvoices = isInvoiceDerived(cat);
                const catInvoices = invoicesByCategory[cat.id] || [];
                const catVariance = Math.round((planned - effActual) * 100) / 100;
                const spentPct = planned > 0 ? Math.min((effActual / planned) * 100, 100) : 0;
                const recs = (recsMap[cat.id] || []).filter(
                  (r) => !dismissed[r.id] && planned > r.proposedCost
                );
                const isExpanded = expandedRow === cat.id;
                const catObservations = observationsFor("budget", cat.id);

                return (
                  <React.Fragment key={cat.id}>
                    <tr className="group" style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border-row)' }}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <button onClick={(e) => toggleRow(e, cat.id)} className="text-gray-500 hover:text-gray-300">
                            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                          <span className="text-xs text-gray-300">{cat.name}</span>
                          {catObservations.length > 0 && !isExpanded && (
                            <ObservationFlagIcon observations={catObservations} size="sm" />
                          )}
                          {recs.length > 0 && !isExpanded && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400">{recs.length} tip{recs.length > 1 ? "s" : ""}</span>
                          )}
                          <Attachments budgetId={cat.id} />
                          {linksBadge(cat)}
                          {cat.notes && (
                            <div className="relative flex items-center">
                              <button className="text-gray-600 hover:text-gray-400" onMouseEnter={() => setHoveredNote(cat.id)} onMouseLeave={() => setHoveredNote(null)} aria-label="Show note">
                                <Info className="w-3 h-3" />
                              </button>
                              {hoveredNote === cat.id && (
                                <div className="absolute left-4 top-0 z-50 w-72 px-2.5 py-1.5 rounded-lg text-[10px] text-gray-300 whitespace-normal"
                                  style={{ background: 'var(--bg-card-frosted)', border: '1px solid var(--border-card-frosted)' }}>
                                  {cat.notes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="w-full rounded-full h-0.5 mt-1" style={{ background: 'var(--bg-progress-track)', marginLeft: recs.length > 0 ? '1.25rem' : 0 }}>
                          <div className={`h-0.5 rounded-full ${spentPct > 90 ? "bg-red-500/60" : "bg-blue-500/40"}`} style={{ width: `${spentPct}%` }} />
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {byCategory[cat.id] ? (
                          <div className="flex items-center justify-end gap-1.5">
                            {itemizedBadge(cat)}
                            <span className="w-24 text-right text-xs font-mono text-blue-300 px-1.5 py-0.5">{formatMoney((cat.estimatedMat || 0))}</span>
                          </div>
                        ) : (
                          <MoneyInput value={(cat.estimatedMat || 0)} onChange={(v) => updateMat(cat.id, v)}
                            className="w-24 text-right text-xs font-mono text-blue-300 bg-transparent border border-transparent rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                            style={{ borderColor: 'transparent' }} />
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <MoneyInput value={(cat.estimatedLabor || 0)} onChange={(v) => updateLabor(cat.id, v)}
                          className="w-24 text-right text-xs font-mono text-purple-300 bg-transparent border border-transparent rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                          style={{ borderColor: 'transparent' }} />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {fromInvoices && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 font-semibold" title={`Paid on ${catInvoices.length} invoice${catInvoices.length === 1 ? "" : "s"}`}>
                              ✦{catInvoices.length}
                            </span>
                          )}
                          <MoneyInput value={(fromInvoices ? effActual : cat.actual)} onChange={(v) => updateActual(cat.id, v)}
                            className={`w-24 text-right text-xs font-mono bg-transparent border border-transparent rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 ${fromInvoices ? "text-indigo-300" : "text-gray-200"}`}
                            style={{ borderColor: 'transparent' }} />
                        </div>
                        {dueFor(cat) > 0 && (
                          <div className="text-[10px] font-mono mt-0.5 pr-1.5 whitespace-nowrap">
                            <span className="text-amber-400 font-semibold">{formatMoney(dueFor(cat))} due</span>
                            <span className="text-gray-500"> of {formatMoney(invoicedFor(cat))} invoiced</span>
                          </div>
                        )}
                      </td>
                      <td className={`px-4 py-2.5 text-right text-xs font-mono font-semibold ${catVariance >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {formatMoney(catVariance)}
                      </td>
                      <td className="px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => deleteCategory(cat.id)} className="text-gray-600 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ borderBottom: '1px solid var(--border-row)' }}>
                        <td colSpan={6} className="px-4 pb-3 pt-0">
                          <div className="space-y-2 ml-5">
                            {notesField(cat, "text-xs")}
                            {linksField(cat)}
                            {catInvoices.length > 0 && (
                              <div className="rounded-lg p-2.5" style={{ background: 'var(--bg-row-hover)', border: '1px solid var(--border-row)' }}>
                                <p className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-1.5">
                                  ✦ From invoices ({catInvoices.length})
                                </p>
                                <div className="space-y-0.5">
                                  {catInvoices.map((iv) => (
                                    <a
                                      key={iv.id + iv.amount}
                                      href={iv.pdfDownloadURL}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-white/[0.04] transition-colors"
                                    >
                                      <span className="truncate text-gray-300">{iv.vendor || "—"} · <span className="text-gray-500">{iv.status}</span></span>
                                      <span className="font-mono text-cyan-400">{formatMoney(iv.amount)}</span>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            {catObservations.map((obs, i) => (
                              <AIObservationInlineCard
                                key={`obs-${i}`}
                                observation={obs}
                                entityLabel={`the ${cat.name} budget line`}
                              />
                            ))}
                            {recs.map((rec) => (
                              <div key={rec.id} className="flex items-start gap-3 p-2.5 rounded-lg" style={{ background: 'var(--bg-row-hover)', border: '1px solid var(--border-row)' }}>
                                <Lightbulb className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <p className="text-xs font-semibold text-gray-200">{rec.title}</p>
                                    <span className={`text-[10px] px-1 py-0.5 rounded ${riskColors[rec.risk]}`}>{rec.risk} risk</span>
                                    <span className={`text-[10px] px-1 py-0.5 rounded ${effortColors[rec.effort]}`}>{rec.effort} effort</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mb-1.5">{rec.description}</p>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-500">{formatMoney(rec.currentCost)} → <span className="text-emerald-400 font-mono font-semibold">{formatMoney(rec.proposedCost)}</span></span>
                                    <span className="text-xs font-mono font-semibold text-emerald-400">save {formatMoney(rec.savings)}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <button onClick={() => approveSaving(rec)} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"><Check className="w-3 h-3" /> Approve</button>
                                  <button onClick={() => dismissSaving(rec.id)} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md text-gray-400 hover:text-gray-200 transition-colors" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)' }}><X className="w-3 h-3" /></button>
                                </div>
                              </div>
                            ))}
                            <PlanLineItems category={cat} items={lineItems} taxRate={taxRate} setItems={setLineItems} manualEstimate={manualMat(cat.id)} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td className="px-4 py-3 text-xs font-semibold text-gray-200">Total</td>
                <td className="px-4 py-3 text-right text-xs font-mono font-semibold text-blue-300">{formatMoney(totalMat)}</td>
                <td className="px-4 py-3 text-right text-xs font-mono font-semibold text-purple-300">{formatMoney(totalLabor)}</td>
                <td className="px-4 py-3 text-right text-xs font-mono font-semibold text-gray-200">{formatMoney(totalActual)}</td>
                <td className={`px-4 py-3 text-right text-xs font-mono font-semibold ${variance >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatMoney(variance)}</td>
                <td></td>
              </tr>
              {/* Jira-style quick-add row */}
              <tr>
                <td colSpan={6} className="px-4 py-2" style={{ borderTop: '1px dashed var(--border-row)' }}>
                  <div className="flex items-center gap-2">
                    <Plus className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                    <input ref={quickRef} type="text" value={quickName}
                      onChange={(e) => setQuickName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") quickAdd(); }}
                      placeholder="Add item… press Enter to save"
                      className="flex-1 text-xs bg-transparent focus:outline-none py-1 text-gray-300 placeholder-gray-600" />
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </InfoBentoCard>
    </>
  );
}

export default function Budget({ budget, setBudget }) {
  const [dismissed, setDismissed] = useFirestoreDoc("savings-dismissed-b", {});
  const { items: invoices } = useInvoices();
  const shopping = useShoppingList();

  // budgetId -> { invoiced, paid } across all invoices for that category
  const invoiceTotals = invoiceTotalsByCategory(invoices);
  const actualFromInvoices = Object.fromEntries(
    Object.entries(invoiceTotals).map(([id, t]) => [id, t.invoiced])
  );

  // budgetId -> short list of invoices that contributed
  const invoicesByCategory = {};
  invoices.forEach((inv) => {
    (inv.allocations || []).forEach((a) => {
      const arr = invoicesByCategory[a.budgetCategoryId] || (invoicesByCategory[a.budgetCategoryId] = []);
      arr.push({
        id: inv.id,
        vendor: inv.vendor,
        amount: a.amount,
        pdfDownloadURL: inv.pdfDownloadURL,
        status: inv.status,
      });
    });
  });

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <InfoBentoCard
        cornerGlow
        icon={<DollarSign className="w-4 h-4" />}
        accent="cyan"
        title="Budget"
        subtitle={`${planBSqFt} sq ft expansion · Santa Clara County`}
        className="mb-6"
      />

      {/* Content */}
      <BudgetTable
        budget={budget}
        setBudget={setBudget}
        sqFt={planBSqFt}
        dismissed={dismissed}
        setDismissed={setDismissed}
        actualFromInvoices={actualFromInvoices}
        invoiceTotals={invoiceTotals}
        invoicesByCategory={invoicesByCategory}
        shopping={shopping}
      />
    </div>
  );
}
